<template>
  <div>
    <Navbar v-if="$route.name === 'events'" />
    <Breadcrumb v-if="$route.name === 'events'" />
    <div class="px-3">
      <div class="row mt-3 mb-3">
        <div class="col-8">
          <div class="d-flex flex-nowrap flex-wrap" style="gap: 0.5rem;">
            <button @click.prevent="newEvent" class="btn btn-primary gdq_faded gdq-font-size-80" style="width: 16.66%;">
              <span class="icon-plus me-2"></span>Add event
            </button>
            <select v-model="filters.status" @change="applyFilters" class="form-select gdq-font-size-80" style="width: 16.66%;">
              <option value="">Status</option>
              <option value="draft">Draft</option>
              <option value="public">Public</option>
              <option value="past">Past</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <select v-model="filters.category_id" @change="applyFilters" class="form-select gdq-font-size-80" style="width: 16.66%;">
              <option :value="0">Category</option>
              <option v-for="c in categories" :key="c.id" :value="c.id">{{ c.name }}</option>
            </select>
            <input type="date" v-model="filters.from" @change="applyFilters" class="form-control gdq-font-size-80" style="width: 16.66%;" title="From date" />
            <input type="date" v-model="filters.to" @change="applyFilters" class="form-control gdq-font-size-80" style="width: 16.66%;" title="To date" />
          </div>
        </div>
        <div class="col-4">
          <div class="input-group input-group-sm">
            <input v-model="filters.search" placeholder="Search title / slug..." type="text" @input="searchDebounced" class="form-control gdq-font-size-80" autocomplete="off" />
            <button class="btn btn-outline-secondary" type="button" @click="filters.search=''; applyFilters()">X</button>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-12 mb-3 text-start">Number of events in the list: {{ total }}</div>
      </div>

      <div class="table-responsive">
        <table class="table table-hover align-middle">
          <thead>
            <tr>
              <th>ID</th>
              <th>Title</th>
              <th>Category</th>
              <th>Dates</th>
              <th>Location</th>
              <th>Language</th>
              <th>Seats</th>
              <th>Bookings</th>
              <th>Status</th>
              <th class="text-end">Actions</th>
            </tr>
          </thead>
          <tbody v-if="loading">
            <tr>
              <td colspan="10" class="text-center text-primary my-2">
                <div class="spinner-border spinner-border-sm" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
              </td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr v-for="e in events" :key="e.id" style="cursor: pointer;" @click="editEvent(e)">
              <td>{{ e.id }}</td>
              <td>{{ e.title_en || e.title_sv || e.slug }}</td>
              <td>{{ e.category_name || '-' }}</td>
              <td>{{ fmtDates(e) }}</td>
              <td>{{ e.is_virtual ? 'Virtual' : (e.location_city || e.location || '-') }}</td>
              <td>{{ langName(e.event_language_id) }}</td>
              <td>{{ e.booked }} / {{ e.effective_capacity || e.capacity }}</td>
              <td>{{ e.bookings }}</td>
              <td><span :class="statusBadge(e.status)" style="font-size: 0.875rem;">{{ capStatus(e.status) }}</span></td>
              <td class="text-end">
                <span
                  class="me-2"
                  style="cursor: pointer;"
                  title="View/edit bookings"
                  @click.prevent.stop="viewBookings(e)"
                >
                  <fa-icon name="users" />
                </span>
              </td>
            </tr>
            <tr v-if="!loading && events.length === 0">
              <td colspan="10" class="text-center text-muted py-4">No events found</td>
            </tr>
          </tbody>
        </table>
      </div>

      <Pagination :current-page="page" :per-page="perPage" :total-rows="total" aria-label="Events pagination" @page-changed="goPage" />
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import Navbar from './Navbar.vue'
import Breadcrumb from './Breadcrumb.vue'
import Pagination from './Pagination.vue'

export default {
  name: 'Events',
  components: { Navbar, Breadcrumb, Pagination },
  data () {
    return {
      filters: {
        search: '',
        category_id: 0,
        status: '',
        from: '',
        to: ''
      },
      categories: [],
      languages: [],
      events: [],
      total: 0,
      page: 1,
      perPage: 10,
      loading: false
    }
  },
  created () {
    this.categories_get()
    this.languages_get()
    this.events_get()
  },
  methods: {
    searchDebounced: _.debounce(function () {
      this.applyFilters()
    }, 400),
    applyFilters () {
      this.page = 1
      this.events_get()
    },
    goPage (p) {
      if (p < 1) return
      this.page = p
      this.events_get()
    },
    async categories_get () {
      try {
        const res = await this.$store.dispatch('event_categories', { lang: 'en' })
        this.categories = res.data.result
      } catch (e) { this.categories = [] }
    },
    async languages_get () {
      try {
        const res = await this.$store.dispatch('languages_fetch')
        this.languages = res.data.result
      } catch (e) { this.languages = [] }
    },
    async events_get () {
      this.loading = true
      try {
        const res = await this.$store.dispatch('events_admin_list', {
          search: this.filters.search,
          category_id: this.filters.category_id,
          status: this.filters.status,
          from: this.filters.from,
          to: this.filters.to,
          start: this.page,
          limit: this.perPage
        })
        this.events = res.data.result
        this.total = res.data.total
      } catch (e) {
        this.events = []
        this.total = 0
      }
      this.loading = false
    },
    langName (id) {
      if (!id) return '-'
      const l = this.languages.find(x => x.id === id)
      return l ? l.language_name : '-'
    },
    statusBadge (s) {
      const map = {
        draft: 'badge bg-warning text-dark',
        public: 'badge bg-success text-dark',
        past: 'badge bg-light text-dark',
        cancelled: 'badge bg-danger'
      }
      return map[s] || 'badge bg-light text-dark'
    },
    capStatus (s) {
      return s ? s.charAt(0).toUpperCase() + s.slice(1) : s
    },
    fmtDates (e) {
      if (!e.start_dt) return '-'
      const s = String(e.start_dt).substring(0, 10)
      const se = e.end_dt ? String(e.end_dt).substring(0, 10) : ''
      if (se && se !== s) return s + ' – ' + se
      if (!e.all_day) {
        const t = String(e.start_dt).substring(11, 16)
        if (t) return s + ' ' + t
      }
      return s
    },
    editEvent (e) {
      this.$router.push('/event/' + e.id)
    },
    viewBookings (e) {
      this.$router.push('/bookings?event=' + e.id)
    },
    newEvent () {
      this.$router.push('/event/-1')
    }
  }
}
</script>
